import React from 'react';
import {Dimensions, ScrollView, View} from 'react-native';
import {Icon, makeStyles} from '@rneui/themed';

import BackButton from '../../../components/rewards/BackButton';
import Button from '../../../components/themed/Button';
import ActivityIndicator from '../../../components/ui-controls/ActivityIndicator';
import BackChevron from '../../../components/ui-controls/BackChevron';
import ContentView from '../../../components/ui-controls/layout/ContentView';
import Markdown from '../../../components/ui-controls/Markdown';
import PressableWithOpacityFeedback from '../../../components/ui-controls/PressableWithOpacityFeedback';
import {universal} from '../../../design-system/tokens';
import {AppRoute} from '../../../models/AppRoute';
import {InformationContentType, RouteNavigatorScreenProps} from '../../../models/navigationTypes';
import * as RootNavigation from '../../../navigation/RootNavigation';
import {useStores} from '../../../stores/use-stores';
import apiService from '../../../transport/apiService';
import SignUpPageLayout, {
  CTA_HEIGHT,
  CTA_RADIUS,
  useWideLayout,
} from '../../account/signUp/SignUpPageLayout';
import {goToActivateSuccess} from '../resetToHome';
import {useAirtableInformation} from '../TermsAndConditions';

const useStyles = makeStyles(theme => ({
  icon: {color: theme.colors?.legacy?.uiControls?.primary},
}));

export default function RewardsActivateTermsAndConditionsScreen({
  route,
  navigation,
}: RouteNavigatorScreenProps<AppRoute.REWARDS_ACTIVATE_TERMS_AND_CONDITIONS>) {
  const {isLoading, information} = useAirtableInformation(
    InformationContentType.REWARDS_PROGRAM_TERMS_AND_CONDITIONS,
  );

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const {userStore, rewardsStore, cartStore, shopSelectionStore} = useStores();
  const phoneNumber = userStore.phoneNumber || '';

  const handleFinishSigningUp = async () => {
    setIsSubmitting(true);

    // Check if user already has a rewards card (e.g. from prior lite sign-up)
    // If so, skip the opt-in verification — they already have a card
    await rewardsStore.loadRewardsForUser(phoneNumber);
    if (rewardsStore.hasRewards) {
      // Reload cart to apply rewards pricing
      cartStore.fullyReloadOrderFromMFL();
      goToActivateSuccess(navigation, {userStore, shopSelectionStore});
      setIsSubmitting(false);
      return;
    }

    if (await apiService.rewardsVerifyExistingUserPhoneNumber({phoneNumber})) {
      navigation.navigate(AppRoute.REWARDS_ACTIVATE_VERIFICATION, route.params);
    }
    setIsSubmitting(false);
  };

  const styles = useStyles();
  const {isDesktopCard: isWide, onLayout} = useWideLayout();

  const leave = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      // Navigate from ROOT_REWARDS_FLOW to ROOT > MAIN > HOME
      RootNavigation.navigate(AppRoute.ROOT, {
        screen: AppRoute.MAIN,
        params: {screen: AppRoute.HOME},
      });
    }
  };

  // One element for both widths. Wide takes the sign-up CTA geometry; narrow
  // omits the key, because withTheme merges props over the theme (SuccessLoyalty).
  const agreeButton = (
    <Button
      loading={isSubmitting}
      disabled={isSubmitting}
      variant="outlined"
      {...(isWide
        ? {buttonStyle: {width: '100%', height: CTA_HEIGHT, borderRadius: CTA_RADIUS}}
        : {})}
      onPress={handleFinishSigningUp}>
      AGREE AND FINISH SIGNING UP
    </Button>
  );

  // DIS-2006: desktop web uses the sign-up centred card, as the join
  // verification step does, instead of a full-width column. The policy text
  // sits at the card's text width; AGREE sits in the form column. The page
  // scrolls as a whole, so there is no inner ScrollView.
  if (isWide) {
    return (
      <ScrollView style={{backgroundColor: universal.white}}>
        <SignUpPageLayout
          centred
          isWide={isWide}
          onLayout={onLayout}
          testID="rewards-activate-terms-and-conditions-screen"
          control={<BackChevron onPress={leave} />}
          intro={
            <>
              {!!isLoading && <ActivityIndicator />}
              {!!information?.content && <Markdown>{information.content}</Markdown>}
            </>
          }
          aside={information?.content ? agreeButton : undefined}
        />
      </ScrollView>
    );
  }

  return (
    <ContentView testID="rewards-activate-terms-and-conditions-screen" onLayout={onLayout}>
      <View
        style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 10,
        }}>
        <BackButton onPress={leave} />
        <PressableWithOpacityFeedback onPress={leave}>
          <Icon
            type="material-icons"
            name="close"
            color={styles.icon.color}
            // style={styles.icon}
            size={36}
          />
        </PressableWithOpacityFeedback>
      </View>
      {!!isLoading && <ActivityIndicator />}
      {!!information?.content && (
        <>
          <ScrollView
            style={{
              paddingBottom: 40,
              height: Dimensions.get('window').height - 150,
            }}>
            <Markdown>{information.content}</Markdown>
          </ScrollView>
          <View style={{flex: 1}} />
          <View
            style={{
              backgroundColor: 'white',
              paddingVertical: 20,
              paddingHorizontal: 10,
              bottom: 0,
              left: 0,
              right: 0,
            }}>
            {agreeButton}
          </View>
        </>
      )}
    </ContentView>
  );
}
